import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import type { ExpensePolicy } from "@/api/types";
import { Icon } from "@/components/Icon";
import { blankable, defaultHelp, type HelpKey, HelpTexts } from "@/help";

/**
 * The wording of the app's (i) tips. Only the tips that differ from the app's own wording are
 * saved; a cleared box goes back to it, or shows nothing for the tips that may be left out.
 */
export function InstructionsPage() {
  const api = useApi();
  const { data, error, loading, reload } = useApiQuery<ExpensePolicy>("/api/expenses/policy");
  const [edits, setEdits] = useState<Partial<Record<HelpKey, string>>>({});
  const [result, setResult] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);

  if (loading && !data) {
    return (
      <div className="card" aria-busy="true">
        <div className="skeleton line" />
        <div className="skeleton line wide" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="error" role="alert">
        {error?.message ?? "Could not load the instructions."}
      </div>
    );
  }

  const policy = data;
  const textOf = (key: HelpKey) => edits[key] ?? policy.helpText?.[key] ?? defaultHelp(key);

  function save() {
    const helpText: Record<string, string> = {};
    for (const help of HelpTexts) {
      const text = textOf(help.key).trim();
      if (text === "" ? blankable(help.key) : text !== defaultHelp(help.key)) {
        helpText[help.key] = text;
      }
    }
    setBusy(true);
    setResult(undefined);
    api
      .put("/api/expenses/policy", {
        mileageRateUsd: policy.mileageRateUsd,
        accountingMailbox: policy.accountingMailbox,
        helpText,
      })
      .then(() => {
        setEdits({});
        setResult({ ok: true, text: "Saved. The tips show the new wording right away." });
        reload();
      })
      .catch((caught: unknown) =>
        setResult({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(false));
  }

  const saveButton = (
    <button type="button" className="btn" onClick={save} disabled={busy}>
      {busy ? "Saving…" : "Save"}
    </button>
  );
  const saved = result && (
    <div className={result.ok ? "success" : "error"} role="status">
      {result.text}
    </div>
  );

  return (
    <div className="narrow">
      <Link className="back" to="/settings">
        <Icon name="chevronLeft" size={18} /> Settings
      </Link>
      <div className="page-head">
        <h1>Instructions</h1>
        {saveButton}
      </div>
      {saved}
      <p className="muted lead">
        The wording of the (i) tips. People can turn them on or off with the Tips switch at the top.
        Separate paragraphs with a blank line; clear a box to go back to the standard wording.
      </p>

      <section className="card stack instructions-card">
        {HelpTexts.map((help) => (
          <label key={help.key}>
            {help.label}
            <textarea
              value={textOf(help.key)}
              onChange={(e) => setEdits({ ...edits, [help.key]: e.target.value })}
              rows={help.text.includes("\n\n") ? 6 : 2}
              maxLength={2000}
            />
            {blankable(help.key) && <span className="hint">Leave it blank to show nothing.</span>}
          </label>
        ))}
      </section>

      {saved}
      {saveButton}
    </div>
  );
}
